




























import { createComponent, onMounted, onUnmounted, ref, inject } from '@vue/composition-api'

import AppButton from '@/components/AppButton.vue'
import AppNavbar from '@/components/AppNavbar.vue'
import AppTextField from '@/components/AppTextField.vue'
import DisplaySettingBottomSheet from '@/components/DisplaySettingBottomSheet.vue'

import { initializeMap, MapFactoryController, getFactoryStatus } from '../lib/map'
import { getFactories } from '../api'
import { MainMapControllerSymbol } from '../symbols'
import { Feature, Overlay } from 'ol'
import Point from 'ol/geom/Point'
import OverlayPositioning from 'ol/OverlayPositioning'
import { defaultFactoryDisplayStatuses, FactoryDisplayStatusType, getDisplayStatusColor, getDisplayStatusText } from '../types'
import { useGA } from '@/lib/useGA'
import { useModalState } from '../lib/hooks'
import { useAppState } from '../lib/appState'
import { useAlertState } from '../lib/useAlert'
import { moveToSharedFactory, permalink } from '../lib/permalink'
import { waitNextTick } from '../lib/utils'

export default createComponent({
  components: {
    AppButton,
    AppNavbar,
    AppTextField,
    DisplaySettingBottomSheet
  },
  props: {
    setFactoryLocation: {
      type: Function,
      required: true
    }
  },
  setup (props, context) {
    const { event } = useGA()
    const root = ref<HTMLElement>(null)
    const popup = ref<HTMLDivElement>(null)
    const mapControllerRef = inject(MainMapControllerSymbol, ref<MapFactoryController>())

    const [, modalActions] = useModalState()
    const [appState, { openEditFactoryForm, pageTransition, expandFactoryDetail }] = useAppState()
    const [, alertActions] = useAlertState()

    const openFactoryDetail = (feature: Feature) => {
      if (!mapControllerRef.value) return

      const factoryId = feature.get('factoryId') as string
      const factory = mapControllerRef.value.getFactory(factoryId)

      if (factory) {
        factory.feature = feature
        appState.factoryData = factory
      }
    }

    const resizeMap = function () {
      if (mapControllerRef.value) {
        mapControllerRef.value.mapInstance.map.updateSize()
      }
    }

    const onClickEditFactoryData = () => {
      if (!appState.factoryData) {
        return
      }

      openEditFactoryForm(appState.factoryData)
    }

    onMounted(() => {
      const popupOverlay = new Overlay({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        element: popup.value!,
        positioning: OverlayPositioning.BOTTOM_LEFT
      })

      const onClickFactoryFeature = async (_: [number, number], feature?: Feature) => {
        // unset active factory data style
        if (appState.factoryData?.feature?.get('defaultStyle')) {
          appState.factoryData?.feature?.setStyle(appState.factoryData?.feature?.get('defaultStyle'))
        }

        if (feature) {
          event('clickFactoryPin')
          if (feature.get('factoryId')) {
            openFactoryDetail(feature)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((context.root as any).$vuetify.breakpoint.mdAndUp) {
              expandFactoryDetail()
            }
          } else {
            // eslint-disable-next-line
            // @ts-ignore
            const p: Point = feature.getGeometry()
            // eslint-disable-next-line
            // @ts-ignore
            const c: Coordinate = p.getCoordinates()
            mapControllerRef?.value?.mapInstance?.map?.getView().setCenter(c)
            const zoom = mapControllerRef?.value?.mapInstance?.map?.getView().getZoom()
            mapControllerRef?.value?.mapInstance?.map?.getView().setZoom((zoom || 0) + 1)
          }
        } else {
          appState.factoryData = null
        }

        // Workaround map resizing issue
        await waitNextTick(context)
        resizeMap()
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const mapController = initializeMap(root.value!, {
        onMoved: async function ([longitude, latitude, range, zoom], canPlaceFactory) {
          permalink.lat(latitude)
          permalink.lng(longitude)
          permalink.zoom(zoom)
          window.location.hash = permalink.dumps()

          appState.canPlaceFactory = canPlaceFactory
          appState.mapLngLat = [longitude, latitude]

          event('moveMap')
          try {
            const factories = await getFactories(range, longitude, latitude)
            if (Array.isArray(factories)) {
              mapController.addFactories(factories)
            }
          } catch (e) {
            // TODO: handle here
          }
        }, // TODO: do on start move to lock selection
        onClicked: onClickFactoryFeature,
        onZoomed: function (zoom) {
          permalink.zoom(zoom)
          window.location.hash = permalink.dumps()
        }
      }, {
        getInitialLocation: function () {
          permalink.load(window.location)

          if (permalink.dumps() !== '') {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return [permalink.lng()!, permalink.lat()!, permalink.zoom()!]
          }
        }
      })

      moveToSharedFactory(mapController, window.location, (factoryId) => {
        const factory = mapControllerRef?.value?.getFactory(factoryId)
        if (factory) {
          const feature = mapController.getFactoriesLayerForStatus(getFactoryStatus(factory)).getFeatureById(factoryId)
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
          onClickFactoryFeature([0, 0], feature as Feature)
        }
      })

      mapController.mapInstance.map.addOverlay(popupOverlay)
      mapControllerRef.value = mapController

      mapController.mapInstance.setLUILayerVisible(false)
    })

    onMounted(() => {
      window.addEventListener('resize', resizeMap)
    })

    onUnmounted(() => {
      window.removeEventListener('resize', resizeMap)
    })

    function onClickCreateFactoryButton () {
      if (!mapControllerRef.value) return

      mapControllerRef.value.mapInstance.setLUILayerVisible(true)

      pageTransition.startCreateFactory()
    }

    const appliedFilters = ref<FactoryDisplayStatusType[]>(defaultFactoryDisplayStatuses)
    const checkActive = (status: FactoryDisplayStatusType) => appliedFilters.value.includes(status)

    const onClickFilterButton = (status: FactoryDisplayStatusType) => {
      if (!mapControllerRef.value) {
        return
      }

      if (checkActive(status)) {
        appliedFilters.value = appliedFilters.value.filter(f => f !== status)
      } else {
        appliedFilters.value = [
          ...appliedFilters.value,
          status
        ]
      }

      mapControllerRef.value.mapInstance.map.getLayers().forEach((layer) => {
        const factoryStatus = layer.get('factoryStatus')
        if (factoryStatus === undefined) return
        const comparableFactoryStatus = factoryStatus === 'default' ? factoryStatus : Number(factoryStatus)
        if (!appliedFilters.value.includes(comparableFactoryStatus)) {
          layer.setVisible(false)
        } else {
          layer.setVisible(true)
        }
      })
    }

    const filterButtonsData = defaultFactoryDisplayStatuses.map(v => ({
      text: getDisplayStatusText(v),
      color: getDisplayStatusColor(v),
      value: v,
      name: v
    }))

    return {
      root,
      modalActions,
      popup,
      zoomToGeolocation: function () {
        if (mapControllerRef.value) {
          event('zoomToGeolocation')
          try {
            mapControllerRef.value.mapInstance.zoomToGeolocation()
          } catch (err) {
            alertActions.showAlert('您沒開放手機定位權限， \n請開放定位權限以用定位功能。')
          }
        }
      },
      appState,
      onClickEditFactoryData,
      onClickCreateFactoryButton,
      onClickFilterButton,
      filterButtonsData,
      checkActive,
      appliedFilters
    }
  }
})
